// Toasts for incoming nudges. A poke lets you fire back a message; a co-op invite lets you jump in.
// The chime is played by CollabContext when a nudge arrives; these are the on-screen cards.
import { useEffect, useRef } from 'react';
import { IconBell, IconChat, IconClose, IconCursor, IconNudge, IconRefresh } from '../components/icons.js';
import { useCollab, type Toast } from './CollabContext.js';
import { navLeaves } from '../app/nav.js';

const AUTO_MS = 8000;
// a co-op invite waits longer: it arrives with the next poll (up to ~12s late) and is the only way in
const INVITE_MS = 60_000;
/** how long a toast stays; null: until it's dismissed (the new-version notice stays until the refresh). */
const lifeOf = (t: Toast): number | null => ('nudge' in t ? (t.nudge.kind === 'coop' ? INVITE_MS : AUTO_MS) : t.app.sticky ? null : AUTO_MS);
/** the most toasts on screen at once (the newest); older ones wait their turn rather than stack off the top. */
export const MAX_TOASTS = 4;

/** "Service" for /service; the path itself for a page the menu doesn't list. */
const pageName = (path: string) =>
  navLeaves().find((i) => i.path === path)?.label
  ?? (path === '/settings' ? 'Settings' : path.startsWith('/admin') ? 'Administration' : path);

export function NudgeToasts() {
  const { toasts, dismissToast, openDmWith, joinCoop, chatOpen, coopOn } = useCollab();
  const timers = useRef<Map<number, number>>(new Map());

  // schedule an auto-dismiss ONCE per toast, so a newly-arrived nudge doesn't restart the countdown of
  // toasts already on screen (and doesn't leak timers for ones that were dismissed).
  useEffect(() => {
    const present = new Set(toasts.map((t) => t.id));
    // only the toasts on screen count down: one waiting its turn gets its full time once it shows
    for (const t of toasts.slice(-MAX_TOASTS)) {
      const life = lifeOf(t);
      if (life == null || timers.current.has(t.id)) continue;
      timers.current.set(t.id, window.setTimeout(() => { dismissToast(t.id); timers.current.delete(t.id); }, life));
    }
    for (const [id, handle] of timers.current) {
      if (!present.has(id)) { window.clearTimeout(handle); timers.current.delete(id); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts]);

  useEffect(() => () => { timers.current.forEach((h) => window.clearTimeout(h)); timers.current.clear(); }, []);

  if (!toasts.length) return null;
  return (
    // above-banner: on phones and tablets the co-op banner sits at the bottom too, so the stack rises over it
    <div className={`nudge-toasts${chatOpen ? ' beside-chat' : ''}${coopOn ? ' above-banner' : ''}`}>
      {toasts.slice(-MAX_TOASTS).map((t) =>
        'app' in t ? (
          // "notice", never "app": .app is the whole window's class (full height), and once made these fill the screen
          <div key={t.id} className={`nudge-toast notice${t.app.icon === 'update' ? ' update' : ''}`} role="alert">
            <span className="nudge-ic">
              {t.app.icon === 'bell' ? <IconBell size={20} /> : t.app.icon === 'update' ? <IconRefresh size={20} /> : t.app.icon === 'chat' ? <IconChat size={20} /> : <IconNudge size={20} />}
            </span>
            <div className="nudge-body">
              <b>{t.app.title}</b>
              {t.app.body && <span className="muted"> {t.app.body}</span>}
            </div>
            {t.app.actionLabel && t.app.action && (
              <button className="btn btn-sm btn-primary" onClick={() => { t.app.action!(); dismissToast(t.id); }}>{t.app.actionLabel}</button>
            )}
            <button className="nudge-x" onClick={() => dismissToast(t.id)} aria-label="Dismiss" title="Dismiss"><IconClose size={14} /></button>
          </div>
        ) : (
          <div key={t.id} className={`nudge-toast ${t.nudge.kind}`} role="alert">
            <span className="nudge-ic">{t.nudge.kind === 'coop' ? <IconCursor size={20} /> : <IconNudge size={20} />}</span>
            <div className="nudge-body">
              <b>{t.nudge.fromName}</b>
              <span className="muted">{t.nudge.kind === 'coop' ? ` wants to co-op${t.nudge.path ? ` on ${pageName(t.nudge.path)}` : ''}` : ' nudged you'}</span>
            </div>
            {t.nudge.kind === 'coop' ? (
              <button className="btn btn-sm btn-primary" onClick={() => { joinCoop(t.nudge.path, t.nudge.room); dismissToast(t.id); }}>Join</button>
            ) : (
              <button className="btn btn-sm" onClick={() => { openDmWith(t.nudge.fromUid); dismissToast(t.id); }}>Reply</button>
            )}
            <button className="nudge-x" onClick={() => dismissToast(t.id)} aria-label="Dismiss" title="Dismiss"><IconClose size={14} /></button>
          </div>
        ),
      )}
    </div>
  );
}
